import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Image as ImageIcon, Loader2, Sparkles, X } from 'lucide-react';

import { Button } from '@/components/common/Button';
import { useLivelloIndietro } from '@/hooks/useLivelloIndietro';
import { useHaptics } from '@/hooks/useHaptics';
import { cn } from '@/lib/cn';
import { PromptInIngleseHint } from '@/components/avatars/PromptInIngleseHint';

interface Props {
  open: boolean;
  /** Whether the avatar already has the daily skin cap exhausted (passed in by parent). */
  isPending?: boolean;
  onClose: () => void;
  /**
   * Caller dispatches the actual reskin mutation. Carries textPrompt + optional
   * referenceImage File. Backend uses one or the other (newer wins when both).
   */
  onGenerate: (textPrompt: string, referenceImage?: File) => void;
}

const MAX_PROMPT_CHARS = 600;

/**
 * Phase 5 — Skin marketplace modal.
 *
 * Full-screen "Choose style" sheet matching the AnimateLibraryModal UX.
 * Text prompt only in this first cut — image-style upload is the backend's
 * `imageStyleUrl` field but isn't wired in the UI yet (deferred until we
 * have a file-upload primitive in the Mini App stack).
 */
export function ReskinModal({ open, isPending, onClose, onGenerate }: Props) {
  const { t } = useTranslation('avatars');
  useLivelloIndietro(open, onClose);
  const haptics = useHaptics();
  const [prompt, setPrompt] = useState('');
  const [refImage, setRefImage] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  if (!open) return null;

  const trimmed = prompt.trim();
  // Allow generation if we have either a prompt OR an image (Meshy needs one of them).
  const hasUsableInput = (trimmed.length >= 3 && trimmed.length <= MAX_PROMPT_CHARS) || !!refImage;
  const canGenerate = hasUsableInput && !isPending;

  const handleGenerate = () => {
    if (!canGenerate) return;
    haptics.impact('medium');
    onGenerate(trimmed, refImage ?? undefined);
  };

  const onPickRefImage = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    setRefImage(file);
    haptics.selection();
  };

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-bg/95 backdrop-blur">
      <header className="flex items-center justify-between px-4 py-4">
        <div>
          <h1 className="text-lg font-bold text-fg">{t('reskin.title')}</h1>
          <p className="mt-0.5 text-xs text-fg-muted">
            {t('reskin.subtitle')}
          </p>
        </div>
        <Button
          variant="icona"
          size={36}
          tono="pieno"
          aria-label={t('common.close')}
          onClick={() => { haptics.impact('light'); onClose(); }}
        >
          <X className="h-5 w-5" />
        </Button>
      </header>

      <div className="flex-1 overflow-y-auto px-4 pb-32">
        <div className="flex flex-col gap-3">
          <label className="text-xs font-semibold uppercase text-fg-muted" htmlFor="reskin-prompt">
            {t('reskin.describe')}
          </label>
          <textarea
            id="reskin-prompt"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            maxLength={MAX_PROMPT_CHARS}
            placeholder={t('reskin.placeholder')}
            className="min-h-[140px] rounded-xl bg-bg-secondary p-3 text-sm text-fg placeholder:text-fg-muted/60 focus:outline-none"
          />
          <PromptInIngleseHint />
          <p className="text-right text-[11px] text-fg-muted">
            {trimmed.length} / {MAX_PROMPT_CHARS}
          </p>

          {/* Or: image-style reference */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold uppercase text-fg-muted">
              {t('reskin.orImage')}
            </label>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={onPickRefImage}
              className="hidden"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className={cn(
                'flex items-center justify-between gap-2 rounded-xl border border-dashed border-fg-muted/40 px-3 py-2.5 text-left text-xs',
                refImage ? 'bg-brand/10 text-fg' : 'bg-bg-secondary text-fg-muted',
              )}
            >
              <span className="flex items-center gap-2">
                <ImageIcon aria-hidden className="h-4 w-4" />
                {refImage ? refImage.name : t('reskin.pickImage')}
              </span>
              {refImage && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setRefImage(null); }}
                  className="rounded-full bg-bg p-1 text-fg-muted active:opacity-80"
                  aria-label={t('reskin.removeImage')}
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </button>
            <p className="text-[11px] text-fg-muted">
              {t('reskin.imageHint')}
            </p>
          </div>

          <div className="rounded-xl bg-bg-secondary p-3 text-xs leading-relaxed text-fg-muted">
            <p className="font-semibold text-fg">{t('common.tips')}</p>
            <ul className="mt-1 space-y-0.5">
              <li>
                <span className="font-semibold text-fg">{t('reskin.tips.wholeBodyLead')}</span>
                {' '}{t('reskin.tips.wholeBodyRest')}
              </li>
              <li>• {t('reskin.tips.materials')}</li>
              <li>• {t('reskin.tips.atmosphere')}</li>
              <li>• {t('reskin.tips.shape')}</li>
            </ul>
            <p className="mt-2 font-semibold text-fg">{t('reskin.examples.title')}</p>
            <ul className="mt-1 space-y-0.5">
              <li>• {t('reskin.examples.cyberpunk')}</li>
              <li>• {t('reskin.examples.knight')}</li>
              <li>• {t('reskin.examples.pumpkin')}</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="absolute inset-x-0 bottom-0 border-t border-border bg-bg/95 px-4 py-3 backdrop-blur">
        <Button fullWidth onClick={handleGenerate} disabled={!canGenerate}>
          {isPending ? (
            <Loader2 aria-hidden className="h-4 w-4 animate-spin" />
          ) : (
            <Sparkles aria-hidden className="h-4 w-4" />
          )}
          {t('reskin.generate')}
        </Button>
      </div>
    </div>
  );
}
